import { FlexColumn } from "@/components/Flex";
import ChatText from "./ChatText";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChatAgent, ChatSuggestionOpt } from "../types";
import { ChatSuggestionsUponAnswerGenerated, ChatSuggestionsOnSelected, ChatSuggestionsBasic } from "./ChatSuggestions";
import Mountable from "@/components/styled/div/Mountable";
import { spacing } from "@/utils/spacing";
import { useChatEditorCommunicator } from "../hooks/useChatEditorCommunicator";
import CircularButton from "@/components/CircularButton";
import { ArrowDown } from "lucide-react";
import { iconSizes } from "@/utils/iconProps";
import { themeColors } from "@/utils/themeColors";

const ScrollToBottom = ({ isHidden, onClick }: { isHidden: boolean; onClick: () => void }) => {
    if (isHidden) return null;

    return (
        <CircularButton
            onClick={onClick}
            radius="4rem"
            sx={{
                backgroundColor: themeColors.background,
                boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.08)",
                zIndex: 10,
                left: "50%",
                transform: "translateX(-50%)",
                top: "90%",
                position: "sticky",
            }}>
            <ArrowDown size={iconSizes.MD} color={themeColors.neutralBlack} />
        </CircularButton>
    );
};

const useChatScrollBehavior = ({ deps = [] }: { deps?: any[] }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [scrollHeight, setScrollHeight] = useState(0);
    const [isScrollReachBottom, setIsScrollReachBottom] = useState(true);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const detectIsBottomMostScroll = useCallback(
        e => {
            const { scrollHeight, scrollTop, clientHeight } = e.target;
            const bottomOffset = scrollHeight - scrollTop - clientHeight;
            if (bottomOffset <= 80) {
                setIsScrollReachBottom(true);
            } else if (isScrollReachBottom) {
                setIsScrollReachBottom(false);
            }
        },
        [isScrollReachBottom]
    );

    // when chat container's height change, scroll to bottom most if user didn't scroll up
    // biome-ignore lint: useExhaustiveDependencies
    useEffect(() => {
        if (!isScrollReachBottom) {
            return;
        }

        if (ref.current) detectIsBottomMostScroll({ target: ref.current });
    }, [scrollHeight, ...deps]);

    // observer container's rect and detect if scrollHeight change
    useEffect(() => {
        if (!ref.current) return;
        const observedElement = ref.current;

        const observer = new ResizeObserver(() => {
            setScrollHeight(observedElement.scrollHeight);
        });

        observer.observe(observedElement);
        return () => observer.disconnect();
    });

    const scrollToBottom = useCallback(() => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
            ref.current?.scrollTo({ top: ref.current.scrollHeight + 500, behavior: "smooth" });
            scrollTimeoutRef.current = null;
        }, 50);
    }, []);

    return { ref, detectIsBottomMostScroll, isScrollReachBottom, scrollToBottom };
};

const ChatLog = ({
    chatSuggestions,
    disabled,
}: {
    chatSuggestions: {
        basic: { options: ChatSuggestionOpt[]; disabled: boolean };
        uponAnswerGenerated: { options: ChatSuggestionOpt[]; disabled: boolean };
        onSelected: { options: ChatSuggestionOpt[]; disabled: boolean };
    };
    disabled?: boolean;
}) => {
    const chatLog = useChatEditorCommunicator(s => s.chatLog);
    const isWaitingForResponse = useChatEditorCommunicator(s => s.isWaitingForResponse);
    const isStreaming = useChatEditorCommunicator(s => s.isStreaming);
    const streamingText = useChatEditorCommunicator(s => s.streamingText);
    const isEmptyFlashcardSelected = useChatEditorCommunicator(s => s.isEmptyFlashcardSelected);

    const { ref, detectIsBottomMostScroll, isScrollReachBottom, scrollToBottom } = useChatScrollBehavior({
        deps: [chatLog],
    });

    return (
        <FlexColumn
            className="scrollbar"
            ref={ref}
            onScroll={detectIsBottomMostScroll}
            style={{
                position: "relative",
                flex: "1",
                overflowX: "hidden",
                overflowY: "auto",
                padding: "0 2.5rem",
                justifyContent: "space-between",
                overscrollBehavior: "none",
            }}>
            <ScrollToBottom onClick={scrollToBottom} isHidden={isScrollReachBottom} />

            {/* CHAT LIST */}
            <FlexColumn style={{ gap: "1.2rem", marginBottom: spacing.SM }}>
                {chatLog?.map((chat, index) => (
                    <ChatText
                        key={chat.text + index}
                        text={chat.text}
                        type={chat.type}
                        quote={chat.quote}
                        attachments={chat.attachments}
                    />
                ))}

                {/* TEXT LOADING */}
                <Mountable mount={isWaitingForResponse || isStreaming}>
                    <ChatText text={streamingText} type={ChatAgent.BOT} isLoading={isWaitingForResponse} />
                </Mountable>
            </FlexColumn>

            <Mountable mount={!disabled && !isStreaming}>
                <FlexColumn style={{ marginBlockEnd: "1rem" }}>
                    <ChatSuggestionsBasic
                        options={chatSuggestions.basic.options}
                        disabled={chatSuggestions.basic.disabled}
                    />
                    <ChatSuggestionsUponAnswerGenerated
                        options={chatSuggestions.uponAnswerGenerated?.options}
                        disabled={chatSuggestions.uponAnswerGenerated?.disabled}
                    />
                    <ChatSuggestionsOnSelected
                        options={chatSuggestions.onSelected?.options}
                        disabled={chatSuggestions.onSelected?.disabled}
                        style={isEmptyFlashcardSelected ? { display: "flex", flexDirection: "column" } : undefined}
                    />
                </FlexColumn>
            </Mountable>
        </FlexColumn>
    );
};

export default ChatLog;
